import React, { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldCheck, ScrollText, FileCheck, ArrowRight, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const KIND_META = {
    legal_acceptance: { label: "Legal", tone: "emerald", Icon: ScrollText },
    verification_consent: { label: "Consent", tone: "sky", Icon: ShieldCheck },
    verification_run: { label: "Verification", tone: "violet", Icon: FileCheck },
    subscription: { label: "Subscription", tone: "amber", Icon: Sparkles },
};

export default function ActivityPage() {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get("/me/activity?limit=100");
            setEvents(data.events || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div data-testid="activity-page">
            <div className="mb-6">
                <p className="overline">Your paper trail</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Activity
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Every action taken with your account — consent grants, verifications, and legal acceptances. Read-only, chronological.
                </p>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : events.length === 0 ? (
                <div className="card-soft p-10 text-center">
                    <ScrollText size={22} className="mx-auto text-muted-foreground" />
                    <div className="mt-2 font-heading font-semibold">No activity yet.</div>
                    <div className="mt-1 text-xs text-muted-foreground">
                        Consent grants, verifications, and legal acceptances will show up here.
                    </div>
                </div>
            ) : (
                <div className="card-soft overflow-hidden">
                    <div className="divide-y divide-border">
                        {events.map((e, i) => {
                            const meta = KIND_META[e.kind] || { label: e.kind, tone: "muted", Icon: ArrowRight };
                            const Icon = meta.Icon;
                            return (
                                <div key={`${e.kind}-${e.at}-${i}`} className="p-4 flex items-start gap-3" data-testid={`activity-item-${i}`}>
                                    <div className={`shrink-0 rounded-full p-1.5 bg-${meta.tone}-500/15 border border-${meta.tone}-500/30 text-${meta.tone}-600`}>
                                        <Icon size={13} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <Badge className="text-[10px] border border-border bg-muted/40 text-foreground">{meta.label}</Badge>
                                            <span className="text-xs text-muted-foreground whitespace-nowrap">{e.at ? new Date(e.at).toLocaleString() : "—"}</span>
                                        </div>
                                        <div className="mt-1 text-sm text-foreground">{e.detail || e.label}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
